import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis, Area,
} from "recharts";
import {
  ChartBar, Lightning, Warning, Heart, MapPin, Users, Calendar, Sparkle,
  TrendUp, TrendDown, ArrowRight, Crosshair, CurrencyDollar,
} from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Executive Summary", icon: ChartBar },
  { key: "acquisition", label: "Acquisition", icon: TrendUp },
  { key: "lifecycle", label: "Lifecycle", icon: ArrowRight },
  { key: "churn", label: "Churn", icon: Warning },
  { key: "renewals", label: "Renewals & Loyalty", icon: Heart },
  { key: "geographic", label: "Geographic", icon: MapPin },
  { key: "poc", label: "POC Performance", icon: Users },
  { key: "cohort", label: "Cohort Analysis", icon: Calendar },
];

// AssureAI brand-friendly palette (works on white)
const C = {
  primary: "#0F172A",
  accent: "#7C3AED",
  blue: "#3B82F6",
  emerald: "#10B981",
  amber: "#F59E0B",
  red: "#EF4444",
  pink: "#EC4899",
  slate: "#64748B",
  cyan: "#06B6D4",
};
const TIER_C = { Enterprise: C.accent, Premium: C.blue, Standard: C.amber };
const YEAR_C = ["#CBD5E1", "#94A3B8", "#64748B", "#475569", "#334155", "#1E293B", "#0F172A"];

const PERIOD_OPTIONS = [
  { value: "yearly", label: "Yearly" },
  { value: "q1", label: "Q1" },
  { value: "q2", label: "Q2" },
  { value: "q3", label: "Q3" },
  { value: "q4", label: "Q4" },
];

// Inline-object literals frozen once at module scope so recharts components
// don't see a new prop reference on every render. Recharts memoises children
// shallowly — stable props translate directly into skipped re-renders.
const TICK_STYLE = Object.freeze({ fontSize: 11 });
const LEGEND_WRAPPER = Object.freeze({ fontSize: 12 });
const BAR_RADIUS_TOP = Object.freeze([4, 4, 0, 0]);
const BAR_RADIUS_TOP_LG = Object.freeze([6, 6, 0, 0]);

export default function MIS() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    setLoading(true);
    api.get("/mis/all").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-[400px] flex items-center justify-center text-sm text-gray-400">
        Loading MIS dashboard…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
          <Sparkle size={28} weight="fill" className="text-violet-600" />
          Executive MIS
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Board-level analytics across acquisition, churn, renewals, geography, POC performance, and cohort retention.
        </p>
      </div>

      {/* Tab nav */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto pb-px" data-testid="mis-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                active
                  ? "border-violet-600 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
              data-testid={`mis-tab-${t.key}`}
            >
              <Icon size={14} weight={active ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewSection data={data} />}
      {tab === "acquisition" && <AcquisitionSection data={data} />}
      {tab === "lifecycle" && <LifecycleSection data={data} />}
      {tab === "churn" && <ChurnSection data={data} />}
      {tab === "renewals" && <RenewalsLoyaltySection data={data} />}
      {tab === "geographic" && <GeographicSection data={data} />}
      {tab === "poc" && <POCSection data={data} />}
      {tab === "cohort" && <CohortSection data={data} />}
    </div>
  );
}

/* ---------- helpers ---------- */
function fmtPct(v) { return `${(v ?? 0).toFixed(1)}%`; }
function fmtInt(v) { return (v ?? 0).toLocaleString(); }

function Card({ title, subtitle, children, full, testid }) {
  return (
    <div
      className={`bg-white border border-gray-200 rounded-lg p-5 ${full ? "lg:col-span-2" : ""}`}
      data-testid={testid}
    >
      {(title || subtitle) && (
        <div className="mb-4">
          {title && <div className="font-display text-sm font-semibold text-gray-900">{title}</div>}
          {subtitle && <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

function ChartFrame({ height = 300, children }) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      {children}
    </ResponsiveContainer>
  );
}

const tooltipStyle = {
  backgroundColor: "#ffffff",
  border: "1px solid #e5e7eb",
  borderRadius: 6,
  fontSize: 12,
  padding: "6px 10px",
};

function SectionTabs({ value, onChange, options }) {
  return (
    <div className="inline-flex items-center gap-1 bg-gray-100 rounded-md p-0.5 mb-3" data-testid="period-tabs">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            value === o.value ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
          }`}
          data-testid={`period-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ============== OVERVIEW ============== */
function OverviewSection({ data }) {
  const k = data.kpis;
  const kpis = [
    { label: "Total Customers", value: fmtInt(k.total_customers), icon: Users, color: "bg-gray-900" },
    { label: "Active", value: fmtInt(k.active_customers), icon: Heart, color: "bg-emerald-600", tint: "text-emerald-700" },
    { label: "Churned", value: fmtInt(k.churned_customers), icon: Warning, color: "bg-red-600", tint: "text-red-700" },
    { label: "Churn Rate", value: fmtPct(k.churn_rate), icon: TrendDown, color: "bg-amber-600", tint: "text-amber-700" },
    { label: `${k.current_year} Acquisitions`, value: fmtInt(k.current_year_total), icon: TrendUp, color: "bg-violet-600" },
    {
      label: "YoY Growth", value: `${k.yoy_growth_rate > 0 ? "+" : ""}${fmtPct(k.yoy_growth_rate)}`,
      icon: k.yoy_growth_rate >= 0 ? TrendUp : TrendDown,
      color: k.yoy_growth_rate >= 0 ? "bg-emerald-600" : "bg-red-600",
      tint: k.yoy_growth_rate >= 0 ? "text-emerald-700" : "text-red-700",
    },
    { label: "Avg Tenure", value: `${k.avg_tenure_years}y`, icon: Calendar, color: "bg-blue-600" },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mis-kpi-grid">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="bg-white border border-gray-200 rounded-lg p-5 relative overflow-hidden">
              <div className={`absolute top-0 left-0 right-0 h-0.5 ${kpi.color}`} />
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">
                  {kpi.label}
                </span>
                <div className={`w-7 h-7 rounded-md ${kpi.color} text-white flex items-center justify-center`}>
                  <Icon size={14} weight="fill" />
                </div>
              </div>
              <div className={`font-display mt-3 text-[28px] leading-none font-semibold ${kpi.tint || "text-gray-900"} tabular-nums`}>
                {kpi.value}
              </div>
            </div>
          );
        })}
      </div>

      <Card
        title="Strategic insights"
        subtitle="Auto-generated highlights drawn from the latest snapshot."
        testid="mis-insights"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.insights.map((ins, i) => (
            <div
              key={ins.label || i}
              className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-md p-3"
              data-testid={`mis-insight-${i}`}
            >
              <div className="text-[10px] uppercase tracking-[0.15em] text-violet-700 font-semibold mb-1">
                {ins.label}
              </div>
              <div className="text-xs text-gray-700 leading-relaxed">{ins.value}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ============== ACQUISITION ============== */
function AcquisitionSection({ data }) {
  const [period, setPeriod] = useState("yearly");
  const rows = period === "yearly" ? data.acquisition.yearly : data.acquisition.quarterly[period];

  return (
    <div className="space-y-4">
      <Card
        title="New customer acquisition · YoY comparison"
        subtitle="Switch between annual totals and any quarter to spot seasonality."
        testid="acq-card"
      >
        <SectionTabs value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
        <ChartFrame height={350}>
          <BarChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="year" tick={TICK_STYLE} stroke="#94a3b8" />
            <YAxis tick={TICK_STYLE} stroke="#94a3b8" />
            <ReTooltip contentStyle={tooltipStyle} />
            <Bar dataKey="count" radius={BAR_RADIUS_TOP_LG}>
              {rows.map((row, i) => (
                <Cell key={`acq-${row.year ?? i}`} fill={YEAR_C[i % YEAR_C.length]} />
              ))}
            </Bar>
          </BarChart>
        </ChartFrame>
        <div className="mt-3 text-[11px] text-gray-500">
          Period: <span className="font-mono">{period === "yearly" ? "Annual" : period.toUpperCase()}</span> ·{" "}
          Total: <span className="font-mono">{fmtInt(rows.reduce((a, b) => a + b.count, 0))}</span>
        </div>
      </Card>
    </div>
  );
}

/* ============== LIFECYCLE ============== */
function LifecycleSection({ data }) {
  const [period, setPeriod] = useState("yearly");
  const rows = period === "yearly" ? data.lifecycle.yearly : data.lifecycle.quarterly[period];
  const fmt = rows.map((r) => ({ year: r.year, New: r.new, Churned: r.churned, "Net Growth": r.net_growth }));

  return (
    <Card
      title="Customer lifecycle"
      subtitle="New acquisitions vs churned, by activation cohort year."
      testid="lifecycle-card"
    >
      <SectionTabs value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
      <ChartFrame height={360}>
        <ComposedChart data={fmt}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="year" tick={TICK_STYLE} stroke="#94a3b8" />
          <YAxis tick={TICK_STYLE} stroke="#94a3b8" />
          <ReTooltip contentStyle={tooltipStyle} />
          <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
          <Bar dataKey="New" fill={C.emerald} radius={BAR_RADIUS_TOP} />
          <Bar dataKey="Churned" fill={C.red} radius={BAR_RADIUS_TOP} />
          <Line dataKey="Net Growth" stroke={C.accent} strokeWidth={2.5} dot={{ r: 4, fill: C.accent }} />
        </ComposedChart>
      </ChartFrame>
    </Card>
  );
}

/* ============== CHURN ============== */
function ChurnSection({ data }) {
  return (
    <div className="space-y-4">
      <Card
        title="Churn rate by activation cohort"
        subtitle="Of the firms that joined in year X, what % have since churned."
        testid="churn-year-card"
      >
        <ChartFrame height={360}>
          <ComposedChart data={data.churn.by_year}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="year" tick={TICK_STYLE} stroke="#94a3b8" />
            <YAxis yAxisId="left" tickFormatter={(v) => `${v}%`} tick={TICK_STYLE} stroke="#94a3b8" />
            <YAxis yAxisId="right" orientation="right" tick={TICK_STYLE} stroke="#94a3b8" />
            <ReTooltip contentStyle={tooltipStyle} />
            <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
            <Bar yAxisId="right" dataKey="churned" name="Churned" fill={C.amber} radius={BAR_RADIUS_TOP} />
            <Line
              yAxisId="left"
              dataKey="churn_rate"
              name="Churn Rate (%)"
              stroke={C.red}
              strokeWidth={2.5}
              dot={{ r: 5, fill: C.red, stroke: "#fff", strokeWidth: 2 }}
            />
          </ComposedChart>
        </ChartFrame>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Churn by pricing tier" subtitle="Retention performance across tiers." testid="churn-tier-card">
          <ChartFrame height={280}>
            <BarChart data={data.churn.by_tier} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={TICK_STYLE} stroke="#94a3b8" />
              <YAxis type="category" dataKey="tier" width={90} tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <ReTooltip contentStyle={tooltipStyle} formatter={(v, _, p) => [`${v}% (${p.payload.churned}/${p.payload.total})`, "Churn"]} />
              <Bar dataKey="churn_rate" radius={[0, 6, 6, 0]}>
                {data.churn.by_tier.map((r) => (
                  <Cell key={r.tier} fill={TIER_C[r.tier] || C.slate} />
                ))}
              </Bar>
            </BarChart>
          </ChartFrame>
        </Card>

        <Card title="Churn reasons" subtitle="Captured from the structured churn flow." testid="churn-reasons-card">
          {data.churn.by_reason.length === 1 && data.churn.by_reason[0].reason === "Unspecified" ? (
            <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-200 rounded-md p-4 leading-relaxed">
              No structured churn reasons logged yet. Reasons populate as your team marks
              firms via the <span className="font-mono">Admin → Firms → Mark Churned</span> flow.
            </div>
          ) : (
            <div className="space-y-2">
              {data.churn.by_reason.map((r) => (
                <div key={r.reason} className="text-xs" data-testid={`churn-reason-${r.reason}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-700">{r.reason}</span>
                    <span className="font-mono tabular-nums text-gray-700">{r.count} · {r.pct}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-red-500" style={{ width: `${r.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/* ============== RENEWALS & LOYALTY ============== */
function RenewalsLoyaltySection({ data }) {
  const b = data.renewals_loyalty;
  const rows = [
    { name: "First year", value: b.first_year, color: C.blue },
    { name: "Renewed once", value: b.renewed_1x, color: C.emerald },
    { name: "Renewed twice", value: b.renewed_2x, color: C.amber },
    { name: "Renewed 3×", value: b.renewed_3x, color: C.accent },
    { name: "Renewed 4×+", value: b.renewed_4plus, color: C.pink },
  ];
  const total = rows.reduce((a, r) => a + r.value, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card title="Renewal frequency" subtitle="Live firms grouped by # of renewal cycles." testid="loyalty-card">
        <ChartFrame height={300}>
          <PieChart>
            <Pie data={rows} dataKey="value" nameKey="name" innerRadius={60} outerRadius={95} paddingAngle={2}>
              {rows.map((r) => <Cell key={r.name} fill={r.color} />)}
            </Pie>
            <ReTooltip contentStyle={tooltipStyle} formatter={(v, n) => [`${v} firms (${((v / total) * 100).toFixed(1)}%)`, n]} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
          </PieChart>
        </ChartFrame>
      </Card>

      <Card title="Loyalty ladder" subtitle="Each row = one rung of customer maturity." testid="loyalty-ladder" full>
        <div className="space-y-3">
          {rows.map((r) => {
            const pct = total ? (r.value / total) * 100 : 0;
            return (
              <div key={r.name}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                    <span className="font-medium text-gray-700">{r.name}</span>
                  </span>
                  <span className="font-mono tabular-nums">
                    <span className="font-semibold text-gray-900">{r.value}</span>
                    <span className="text-gray-400 ml-2">{pct.toFixed(1)}%</span>
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, background: r.color }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 pt-4 border-t border-gray-100 text-[11px] text-gray-500 leading-relaxed">
          🎯 <strong>Power-user pool</strong>: {b.renewed_2x + b.renewed_3x + b.renewed_4plus} firms have stuck with you for 2+ years.
          These are your case-study candidates.
        </div>
      </Card>
    </div>
  );
}

/* ============== GEOGRAPHIC ============== */
function GeographicSection({ data }) {
  const regionData = data.geographic.regions.map((r) => ({ ...r, label: r.region }));
  const cityRows = data.geographic.cities;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {regionData.map((r) => {
          const churnClass = r.churn_rate < 20
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : r.churn_rate < 30
            ? "bg-amber-50 border-amber-200 text-amber-700"
            : "bg-red-50 border-red-200 text-red-700";
          return (
            <div
              key={r.region}
              className="bg-white border border-gray-200 rounded-lg p-4"
              data-testid={`region-card-${r.region}`}
            >
              <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-2">
                {r.region} Region
              </div>
              <div className="font-display text-[28px] font-semibold text-gray-900 tabular-nums leading-none">
                {fmtInt(r.total)}
              </div>
              <div className="text-[11px] text-gray-500 mt-1">customers</div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 text-xs">
                <span className="text-emerald-700 font-mono">{r.active} active</span>
                <span className={`inline-block px-2 py-0.5 rounded-full font-mono text-[11px] border ${churnClass}`}>
                  {fmtPct(r.churn_rate)} churn
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <Card title="Top 15 cities · customer distribution" subtitle="Active vs churned by city." testid="city-dist-card">
        <ChartFrame height={400}>
          <BarChart data={cityRows} margin={{ left: 20, right: 10, top: 5, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="city" tick={{ fontSize: 10 }} angle={-35} textAnchor="end" stroke="#94a3b8" interval={0} />
            <YAxis tick={TICK_STYLE} stroke="#94a3b8" />
            <ReTooltip contentStyle={tooltipStyle} />
            <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
            <Bar dataKey="active" stackId="a" fill={C.emerald} name="Active" radius={[0, 0, 0, 0]} />
            <Bar dataKey="churned" stackId="a" fill={C.red} name="Churned" radius={BAR_RADIUS_TOP} />
          </BarChart>
        </ChartFrame>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Top 10 cities · churn rate" subtitle="Lower bars = healthier markets." testid="city-churn-card">
          <ChartFrame height={340}>
            <BarChart data={cityRows.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" tickFormatter={(v) => `${v}%`} tick={TICK_STYLE} stroke="#94a3b8" />
              <YAxis type="category" dataKey="city" width={100} tick={TICK_STYLE} stroke="#94a3b8" />
              <ReTooltip contentStyle={tooltipStyle} />
              <Bar dataKey="churn_rate" radius={[0, 4, 4, 0]}>
                {cityRows.slice(0, 10).map((r) => (
                  <Cell
                    key={r.city}
                    fill={r.churn_rate < 15 ? C.emerald : r.churn_rate < 25 ? C.amber : C.red}
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartFrame>
        </Card>

        <Card title="Regional market share" subtitle="Distribution of customers by zone." testid="region-share-card">
          <ChartFrame height={340}>
            <PieChart>
              <Pie data={regionData} dataKey="total" nameKey="label" outerRadius={110} paddingAngle={2}>
                {regionData.map((r, i) => (
                  <Cell key={r.label} fill={[C.blue, C.accent, C.amber, C.pink, C.slate, C.cyan][i % 6]} />
                ))}
              </Pie>
              <ReTooltip contentStyle={tooltipStyle} />
              <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
            </PieChart>
          </ChartFrame>
        </Card>
      </div>
    </div>
  );
}

/* ============== POC PERFORMANCE ============== */
function POCSection({ data }) {
  const pocs = data.poc.rows;
  const trend = data.poc.quarterly_trend;
  const periods = useMemo(() => (pocs[0] ? trend[pocs[0].poc].map((p) => p.period) : []), [pocs, trend]);
  const trendData = useMemo(
    () =>
      periods.map((period, i) => {
        const row = { period };
        pocs.forEach((p) => {
          row[p.poc] = trend[p.poc][i].count;
        });
        return row;
      }),
    [periods, pocs, trend],
  );

  const POC_COLOR = (p) => ({ Shankar: C.blue, Onkar: C.accent, Vaibhav: C.amber, Sasikiran: C.pink, Rajaneesh: C.cyan }[p] || C.slate);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="poc-cards">
        {pocs.map((p, i) => (
          <div key={p.poc} className="bg-white border border-gray-200 rounded-lg p-4 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: POC_COLOR(p.poc) }} />
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-semibold text-gray-900">{p.poc}</div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                #{i + 1}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Total" value={p.total} />
              <Stat label="Active" value={p.active} tint="text-emerald-700" />
              <Stat label="Churn rate" value={fmtPct(p.churn_rate)} tint={p.churn_rate < 15 ? "text-emerald-700" : p.churn_rate < 25 ? "text-amber-700" : "text-red-700"} />
              <Stat label="Avg tenure" value={`${p.avg_tenure}y`} />
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-500 flex items-center gap-2 font-mono">
              <span className="text-violet-700">{p.enterprise} Ent</span>
              <span className="text-gray-300">·</span>
              <span className="text-blue-700">{p.premium} Pre</span>
              <span className="text-gray-300">·</span>
              <span className="text-amber-700">{p.standard} Std</span>
            </div>
          </div>
        ))}
      </div>

      <Card title="POC quarterly acquisition trend" subtitle="Last 8 quarters of new firms by POC." testid="poc-trend-card">
        <ChartFrame height={350}>
          <LineChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} stroke="#94a3b8" />
            <YAxis tick={TICK_STYLE} stroke="#94a3b8" />
            <ReTooltip contentStyle={tooltipStyle} />
            <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
            {pocs.map((p) => (
              <Line
                key={p.poc}
                dataKey={p.poc}
                stroke={POC_COLOR(p.poc)}
                strokeWidth={2.5}
                dot={{ r: 3, fill: POC_COLOR(p.poc) }}
                activeDot={{ r: 5 }}
              />
            ))}
          </LineChart>
        </ChartFrame>
      </Card>

      <Card title="POC tier mix" subtitle="How each POC's portfolio splits across pricing tiers." testid="poc-tier-card">
        <ChartFrame height={300}>
          <BarChart data={pocs}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="poc" tick={TICK_STYLE} stroke="#94a3b8" />
            <YAxis tick={TICK_STYLE} stroke="#94a3b8" />
            <ReTooltip contentStyle={tooltipStyle} />
            <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
            <Bar dataKey="enterprise" name="Enterprise" stackId="a" fill={TIER_C.Enterprise} radius={[0, 0, 0, 0]} />
            <Bar dataKey="premium" name="Premium" stackId="a" fill={TIER_C.Premium} radius={[0, 0, 0, 0]} />
            <Bar dataKey="standard" name="Standard" stackId="a" fill={TIER_C.Standard} radius={BAR_RADIUS_TOP} />
          </BarChart>
        </ChartFrame>
      </Card>
    </div>
  );
}

function Stat({ label, value, tint }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">{label}</div>
      <div className={`font-display text-lg font-semibold tabular-nums ${tint || "text-gray-900"}`}>{value}</div>
    </div>
  );
}

/* ============== COHORT ============== */
function CohortSection({ data }) {
  const cohorts = data.cohort.rows;
  const lifetime = data.cohort.lifetime_distribution;
  const lifetimeRows = [
    { name: "< 1 year", value: lifetime.lt_1yr, color: C.red },
    { name: "1 – 2 years", value: lifetime["1_2yr"], color: C.amber },
    { name: "2 – 3 years", value: lifetime["2_3yr"], color: C.blue },
    { name: "3 – 4 years", value: lifetime["3_4yr"], color: C.accent },
    { name: "4+ years", value: lifetime["4plus"], color: C.emerald },
  ];
  const tierYearly = data.cohort.tier_yearly;
  const tierRows = (tierYearly.Enterprise || []).map((row, i) => ({
    year: row.year,
    Enterprise: row.count,
    Premium: tierYearly.Premium[i]?.count || 0,
    Standard: tierYearly.Standard[i]?.count || 0,
  }));

  return (
    <div className="space-y-4">
      <Card title="Cohort retention rates" subtitle="% of each year's cohort still active today." testid="cohort-rates-card">
        <ChartFrame height={320}>
          <BarChart data={cohorts}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="cohort_year" tick={TICK_STYLE} stroke="#94a3b8" />
            <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 110]} tick={TICK_STYLE} stroke="#94a3b8" />
            <ReTooltip
              contentStyle={tooltipStyle}
              formatter={(v, _, p) => [`${v}% retention`, `Cohort: ${p.payload.cohort_size}, Active: ${p.payload.active}`]}
            />
            <Bar dataKey="retention_rate" radius={BAR_RADIUS_TOP_LG}>
              {cohorts.map((c) => (
                <Cell
                  key={c.cohort_year}
                  fill={c.retention_rate >= 85 ? C.emerald : c.retention_rate >= 60 ? C.amber : C.red}
                />
              ))}
            </Bar>
          </BarChart>
        </ChartFrame>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Customer lifetime distribution" subtitle="Tenure buckets across the entire base." testid="lifetime-card">
          <ChartFrame height={300}>
            <PieChart>
              <Pie data={lifetimeRows} dataKey="value" nameKey="name" innerRadius={55} outerRadius={100} paddingAngle={2}>
                {lifetimeRows.map((r) => <Cell key={r.name} fill={r.color} />)}
              </Pie>
              <ReTooltip contentStyle={tooltipStyle} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ChartFrame>
        </Card>

        <Card title="Tier growth trends" subtitle="Year-on-year tier mix evolution." testid="tier-trends-card">
          <ChartFrame height={300}>
            <LineChart data={tierRows}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="year" tick={TICK_STYLE} stroke="#94a3b8" />
              <YAxis tick={TICK_STYLE} stroke="#94a3b8" />
              <ReTooltip contentStyle={tooltipStyle} />
              <Legend iconType="circle" wrapperStyle={LEGEND_WRAPPER} />
              <Line dataKey="Enterprise" stroke={TIER_C.Enterprise} strokeWidth={2.5} dot={{ r: 3 }} />
              <Line dataKey="Premium" stroke={TIER_C.Premium} strokeWidth={2.5} dot={{ r: 3 }} />
              <Line dataKey="Standard" stroke={TIER_C.Standard} strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ChartFrame>
        </Card>
      </div>

      <Card title="Cohort retention matrix" subtitle="Bird's-eye view of cohort health." testid="cohort-matrix-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-left">
              <tr>
                <Th>Cohort Year</Th>
                <Th>Size</Th>
                <Th>Active</Th>
                <Th>Churned</Th>
                <Th>Retention</Th>
                <Th>Health</Th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map((c) => {
                const tone =
                  c.retention_rate >= 85 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : c.retention_rate >= 60 ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-red-50 text-red-700 border-red-200";
                const label = c.retention_rate >= 85 ? "Excellent" : c.retention_rate >= 60 ? "Good" : "At Risk";
                return (
                  <tr key={c.cohort_year} className="border-b border-gray-100" data-testid={`cohort-row-${c.cohort_year}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{c.cohort_year}</td>
                    <td className="px-4 py-3 font-mono tabular-nums">{c.cohort_size}</td>
                    <td className="px-4 py-3 font-mono tabular-nums text-emerald-700">{c.active}</td>
                    <td className="px-4 py-3 font-mono tabular-nums text-red-700">{c.churned}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-[120px]">
                          <div
                            className={c.retention_rate >= 85 ? "h-full bg-emerald-500" : c.retention_rate >= 60 ? "h-full bg-amber-500" : "h-full bg-red-500"}
                            style={{ width: `${c.retention_rate}%` }}
                          />
                        </div>
                        <span className="font-mono tabular-nums text-sm">{c.retention_rate}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${tone}`}>
                        {label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">{children}</th>;
}
